import { useState, useEffect, useRef, useCallback } from "react";

interface UseElementViewCountTrackingOptions {
  /** 몇 개 요소를 본 후 트리거할지 (기본값: 5) */
  threshold?: number;
  /** 트리거 시 실행할 콜백 함수 */
  onTrigger?: (viewedCount: number, viewedIds: Set<number>) => void;
  /** Intersection Observer threshold (요소가 몇 % 보여야 감지할지, 기본값: 1.0) */
  intersectionThreshold?: number;
  /** 추적 기능을 활성화할지 여부 (기본값: true) */
  enabled?: boolean;
  /** 세션 유지 여부 (기본값: false) */
  enableSessionPersistence?: boolean;
  /** 세션 스토리지 키 (기본값: 자동 생성) */
  sessionStorageKey?: string;
}

interface UseElementViewCountTrackingReturn {
  /** 요소에 observer를 연결하는 함수 */
  attachObserver: (element: HTMLElement | null, elementId: number) => void;
  /** 현재까지 본 요소 수 */
  viewedElementCount: number;
  /** 트리거가 이미 실행되었는지 여부 */
  hasTriggered: boolean;
}

/**
 * 요소 뷰 카운트 추적 훅
 *
 * 사용자가 요소를 스크롤하여 볼 때마다 카운팅하고,
 * 지정된 개수(기본 5개)에 도달하면 콜백을 실행합니다.
 *
 * @example
 * ```tsx
 * const { attachObserver } = useElementViewCountTracking({
 *   threshold: 5,
 *   onTrigger: () => setIsSheetOpen(true),
 *   enabled: isWeb(), // 웹 환경에서만 활성화
 *   enableSessionPersistence: true, // 세션 유지 활성화
 *   sessionStorageKey: "home-download-sheet-shown" // 커스텀 키
 * });
 *
 * return (
 *   <div ref={(el) => attachObserver(el, element.id)}>
 *     <SomeCard />
 *   </div>
 * );
 * ```
 */
export const useElementViewCountTracking = (
  options: UseElementViewCountTrackingOptions = {}
): UseElementViewCountTrackingReturn => {
  const {
    threshold = 5,
    onTrigger,
    intersectionThreshold = 1.0,
    enabled = true,
    enableSessionPersistence = false,
    sessionStorageKey = "element-view-count-tracking-triggered",
  } = options;

  // 세션에서 이미 트리거되었는지 확인
  const isSessionTriggered = enableSessionPersistence
    ? sessionStorage.getItem(sessionStorageKey) === "true"
    : false;

  // 요소 뷰 추적을 위한 상태
  const [viewedElementIds, setViewedElementIds] = useState<Set<number>>(
    new Set()
  );
  const [hasTriggered, setHasTriggered] = useState(isSessionTriggered);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 클로저 문제 해결을 위한 ref (최신 상태값 참조)
  const viewedElementIdsRef = useRef(viewedElementIds);
  const hasTriggeredRef = useRef(hasTriggered);

  useEffect(() => {
    viewedElementIdsRef.current = viewedElementIds;
  }, [viewedElementIds]);

  useEffect(() => {
    hasTriggeredRef.current = hasTriggered;
  }, [hasTriggered]);

  // 요소 뷰 추적 함수
  const handleElementView = useCallback(
    (elementId: number) => {
      const currentViewedElementIds = viewedElementIdsRef.current;
      const currentHasTriggered = hasTriggeredRef.current;

      // 중복 방지: 이미 본 요소이거나 이미 트리거되었으면 무시
      if (!currentViewedElementIds.has(elementId) && !currentHasTriggered) {
        const newViewedElementIds = new Set(currentViewedElementIds);
        newViewedElementIds.add(elementId);
        setViewedElementIds(newViewedElementIds);

        // 임계값에 도달한 경우 콜백 실행
        if (newViewedElementIds.size >= threshold) {
          setHasTriggered(true);

          // 세션 유지가 활성화된 경우 sessionStorage에 저장
          if (enableSessionPersistence) {
            sessionStorage.setItem(sessionStorageKey, "true");
          }

          // 외부 콜백 실행
          onTrigger?.(newViewedElementIds.size, newViewedElementIds);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threshold, onTrigger]
  );

  // Intersection Observer 설정 - 요소가 화면에 보이는지 감지
  useEffect(() => {
    // enabled가 false이거나 세션에서 이미 트리거되었으면 Observer 생성하지 않음
    if (!enabled || isSessionTriggered) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const elementId = parseInt(
            entry.target.getAttribute("data-element-id") || "0"
          );
          // 요소가 지정된 비율만큼 보이면 뷰 카운팅
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= intersectionThreshold &&
            elementId
          ) {
            handleElementView(elementId);
          }
        });
      },
      {
        threshold: [0.5, 0.8, 1.0], // 정확한 감지를 위한 다중 threshold (50%, 80%, 100% 보일 때마다 호출한다는 뜻)
        rootMargin: "0px",
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 요소에 observer 연결하는 함수
  const attachObserver = useCallback(
    (element: HTMLElement | null, elementId: number) => {
      // enabled가 false이거나 세션에서 이미 트리거되었으면 아무것도 하지 않음
      if (!enabled || isSessionTriggered || !element || !observerRef.current) {
        return;
      }

      element.setAttribute("data-element-id", elementId.toString());
      observerRef.current.observe(element);
    },
    [enabled, isSessionTriggered]
  );

  return {
    attachObserver,
    viewedElementCount: viewedElementIds.size,
    hasTriggered,
  };
};
